import React, { useState } from "react";
import {
  PackageCheck,
  Settings,
  ShieldCheck,
  CheckCircle2,
  ShoppingCart,
  AlertCircle,
  Check 
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import ConfiguratorSection from "../components/ConfiguratorSection";

const ProductsPage = ({ navigateTo, favorites, toggleFavorite, productsData, onOrder }) => {
  const [viewMode, setViewMode] = useState("sets");
  const [partFilter, setPartFilter] = useState("all");

  const [configStep, setConfigStep] = useState(1);
  
  const steps = [
    { id: 1, name: "Motor" },
    { id: 2, name: "Baterie" },
    { id: 3, name: "Displej" },
    { id: 4, name: "Brzdy" },
    { id: 5, name: "Montáž" },
  ];

  const allProducts = Object.values(productsData).filter(p => !p.isHidden);
  
  // Řazení pro běžné produkty podle ceny
  const sortByPrice = (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0);

  // CHYTRÉ ŘAZENÍ PRO BATERIE (Nejdřív Volty, pak Cena/Kapacita)
  const sortBatteries = (a, b) => {
    const getVolts = (name) => {
      if (!name) return 999;
      // Najde číslo před písmenem V (např. 36V, 48 V)
      const match = name.match(/(\d+)\s*v/i);
      return match ? parseInt(match[1]) : 999;
    };
    
    const vA = getVolts(a.name);
    const vB = getVolts(b.name);
    
    // Pokud se Volty liší, dej menší Volty první (36 -> 48 -> 52)
    if (vA !== vB) {
      return vA - vB;
    }
    // Pokud jsou Volty stejné, seřaď je od nejlevnější (nejmenší)
    return (Number(a.price) || 0) - (Number(b.price) || 0);
  };
  
  const motors = allProducts.filter((p) => p.type === "motor").sort(sortByPrice);
  const batteries = allProducts.filter((p) => p.type === "battery").sort(sortBatteries);
  const displays = allProducts.filter((p) => p.type === "display").sort(sortByPrice);

  const handleQuickAdd = (motorId, batteryId, displayId) => {
    const brakesPrice = productsData["brakes_sensors"]?.price || 0;

    const configData = {
      motor: productsData[motorId],
      battery: productsData[batteryId],
      display: productsData[displayId],
      brakes: { id: "brakes_sensors", name: "Senzory řazení a brzd", price: brakesPrice },
      assembly: { id: "assy_none", name: "Bez montáže", price: 0 }
    };
    onOrder(configData);
  };

  const citySetPrice = 14888;
  const citySetBasePrice = 16888; 

  const offroadSetPrice = 25998;
  const offroadSetBasePrice = 27998; 

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 text-center">
      <h1 className="text-3xl md:text-4xl font-bold text-[#2a3b4c] mb-8">
        E-shop
      </h1>

      <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12 max-w-xl mx-auto">
        <button
          onClick={() => setViewMode("sets")}
          className={`px-8 py-3.5 rounded-xl font-bold transition flex-1 flex items-center justify-center gap-2 ${
            viewMode === "sets"
              ? "bg-[#2a3b4c] text-white shadow-lg scale-105"
              : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
          }`}
        >
          <PackageCheck className="w-5 h-5" /> Kompletní sady
        </button>
        <button
          onClick={() => setViewMode("parts")}
          className={`px-8 py-3.5 rounded-xl font-bold transition flex-1 flex items-center justify-center gap-2 ${
            viewMode === "parts"
              ? "bg-[#2a3b4c] text-white shadow-lg scale-105"
              : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
          }`}
        >
          <Settings className="w-5 h-5" /> Samostatné díly
        </button>
      </div>

      {viewMode === "sets" && (
        <div className="space-y-16 animate-in fade-in duration-500">
          
          <div className="w-full max-w-3xl mx-auto mb-16 px-4">
            <div className="flex items-center justify-between relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 z-0 rounded-full"></div>
              
              <div 
                className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-green-500 z-0 transition-all duration-500 ease-in-out rounded-full" 
                style={{ width: `${((configStep - 1) / (steps.length - 1)) * 100}%` }}
              ></div>

              {steps.map((step) => {
                const isCompleted = configStep > step.id;
                const isActive = configStep === step.id;

                return (
                  <div key={step.id} className="relative z-10 flex flex-col items-center">
                    <div 
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-extrabold text-sm md:text-base border-4 transition-all duration-300 ${
                        isCompleted 
                          ? "bg-green-500 border-green-500 text-white" 
                          : isActive 
                          ? "bg-white border-green-500 text-green-600 shadow-md"
                          : "bg-white border-slate-300 text-slate-400"
                      }`}
                    >
                      {isCompleted ? <Check className="w-5 h-5 md:w-6 md:h-6" /> : step.id}
                    </div>
                    
                    <span 
                      className={`absolute -bottom-7 md:-bottom-8 text-xs md:text-sm font-bold whitespace-nowrap transition-colors duration-300 ${
                        isActive || isCompleted ? "text-slate-800" : "text-slate-400"
                      }`}
                    >
                      {step.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

           <ConfiguratorSection 
             navigateTo={navigateTo} 
             onOrder={onOrder} 
             favorites={favorites} 
             productsData={productsData}
             currentStep={configStep}
             setCurrentStep={setConfigStep}
           />

          <div>
            <h3 className="text-2xl font-bold text-[#2a3b4c] mb-8">
              Předpřipravené sady pro rychlý nákup
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition duration-300 text-left flex flex-col group">
                <div className="h-56 bg-slate-50 flex items-center justify-center p-6 relative">
                  <div className="absolute top-4 left-4 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide z-10 shadow-sm">Bestseller</div>
                  <img src="/images/sadamesto.png" alt="Městská sada" className="max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-grow">
                  <h4 className="text-2xl font-bold text-[#2a3b4c] mb-2">Sada Město (250W + 13Ah)</h4>
                  <p className="text-slate-500 text-sm mb-6 italic">
                    Nejdostupnější kompletní set, který i tak překvapí dojezdem až 80 km.
                  </p>
                  
                  <div className="mb-6">
                    <h5 className="font-bold text-slate-800 text-sm mb-3 uppercase tracking-wider">Obsah balení:</h5>
                    <ul className="grid grid-cols-1 gap-2">
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Středový motor TSDZ2B 250W</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Baterie 36V 13Ah (články Samsung/LG)</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Kompaktní IPS displej DZ40</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Senzory brzd, kabeláž a příslušenství</li>
                      <li className="flex items-center gap-2 text-sm text-amber-600 font-semibold mt-1"><AlertCircle className="w-4 h-4" /> Sada je dodávána jako stavebnice (bez montáže na kolo)</li>
                    </ul>
                  </div>

                  <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-6">
                    <div>
                      <div className="text-sm text-slate-400 line-through font-bold">{citySetBasePrice.toLocaleString()} Kč</div>
                      <div className="text-3xl font-black text-green-600">{citySetPrice.toLocaleString()} Kč</div>
                    </div>
                    <button
                      onClick={() => handleQuickAdd("mot_250", "bat_36v_13ah", "disp_dz40")}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition shadow-md flex items-center gap-2"
                    >
                      <ShoppingCart className="w-5 h-5" /> Přidat do košíku
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition duration-300 text-left flex flex-col group">
                <div className="h-56 bg-slate-50 flex items-center justify-center p-6 relative">
                  <img src="/images/offroadset.png" alt="Offroad sada" className="max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-grow">
                  <h4 className="text-2xl font-bold text-[#2a3b4c] mb-2">Sada Extrém (1000W + 20Ah)</h4>
                  <p className="text-slate-500 text-sm mb-6 italic">
                    Nekompromisní síla do kopců s masivní baterií a dojezdem až 200 km.
                  </p>

                  <div className="mb-6">
                    <h5 className="font-bold text-slate-800 text-sm mb-3 uppercase tracking-wider">Obsah balení:</h5>
                    <ul className="grid grid-cols-1 gap-2">
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Motor TSDZ16 1000W (High Torque)</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Baterie 52V 20Ah (extrémní kapacita)</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Barevný TFT displej EKD01</li>
                      <li className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> Senzory brzd, kabeláž a příslušenství</li>
                      <li className="flex items-center gap-2 text-sm text-amber-600 font-semibold mt-1"><AlertCircle className="w-4 h-4" /> Sada je dodávána jako stavebnice (bez montáže na kolo)</li>
                    </ul>
                  </div>

                  <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-6">
                    <div>
                      <div className="text-sm text-slate-400 line-through font-bold">{offroadSetBasePrice.toLocaleString()} Kč</div>
                      <div className="text-3xl font-black text-green-600">{offroadSetPrice.toLocaleString()} Kč</div>
                    </div>
                    <button
                      onClick={() => handleQuickAdd("mot_1000", "bat_52v_20ah", "disp_ekd01")}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition shadow-md flex items-center gap-2"
                    >
                      <ShoppingCart className="w-5 h-5" /> Přidat do košíku
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {viewMode === "parts" && (
        <div className="animate-in fade-in duration-500">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
            {[
              { id: "all", label: "Všechny díly" },
              { id: "motors", label: "Motory" },
              { id: "batteries", label: "Baterie" },
              { id: "displays", label: "Displeje" },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setPartFilter(filter.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  partFilter === filter.id
                    ? "bg-green-600 text-white shadow-md scale-105"
                    : "bg-white text-slate-500 border border-slate-200 hover:border-green-400 hover:text-green-600 hover:bg-green-50"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="space-y-16">
            {(partFilter === "all" || partFilter === "motors") &&
              motors.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-[#2a3b4c] mb-8">
                    Motory
                  </h2>
                  <div className="flex flex-wrap justify-center gap-6">
                    {motors.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        navigateTo={navigateTo}
                        favorites={favorites} 
                        toggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                </section>
              )}

            {(partFilter === "all" || partFilter === "batteries") &&
              batteries.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-[#2a3b4c] mb-8">
                    Baterie
                  </h2>
                  <div className="flex flex-wrap justify-center gap-6">
                    {batteries.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        navigateTo={navigateTo}
                        favorites={favorites} 
                        toggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                </section>
              )}

            {(partFilter === "all" || partFilter === "displays") &&
              displays.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-[#2a3b4c] mb-8">
                    Displeje
                  </h2>
                  <div className="flex flex-wrap justify-center gap-6">
                    {displays.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        navigateTo={navigateTo}
                        favorites={favorites} 
                        toggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                </section>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;